import React, { useMemo, useState, useId } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Paper,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruck } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "@mui/material/styles";
import { makeInventorySx } from "./uiStyles";
import { useTranslation } from "react-i18next";
import { getTimeAgo } from "../../utils/dateUtils";

function MaterialReceptionTimeline({
  listings = [],
  requests = [],
  dateFrom,
  dateTo,
  materials = [],
  defaultCollapsed = false, // optional prop if you ever want it collapsed by default
}) {
  const theme = useTheme();
  const sx = makeInventorySx(theme);
  const { t } = useTranslation(); // ✅ ADD

  const collapseId = useId();
  const [open, setOpen] = useState(!defaultCollapsed);

  const events = useMemo(() => {
    if (!Array.isArray(listings) || !Array.isArray(requests)) return [];

    const listingMap = new Map(listings.map((l) => [String(l.id), l]));
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);

    const out = [];

    for (const r of requests) {
      if (!r?.date_delivered) continue;

      const deliveredDate = new Date(r.date_delivered);
      if (from && deliveredDate < from) continue;
      if (to && deliveredDate > to) continue;

      const listing = listingMap.get(String(r.listing_id));
      if (!listing) continue;

      const material =
        listing.material ||
        listing.materialType ||
        listing.material_name ||
        listing.material_type ||
        "—";

      if (materials.length && !materials.includes(material)) continue;

      const donor =
        listing.donor_name ||
        listing.company_name ||
        listing.company ||
        listing.company_id ||
        "—";

      // r.quantity = allocated amount for this request
      const qty = Number(r.quantity || 0);

      out.push({
        id: `${r.listing_id}-${r.date_delivered}`,
        date: deliveredDate,
        dateLabel: deliveredDate.toISOString().slice(0, 10),
        label: donor,
        material,
        weight: qty,
      });
    }

    out.sort((a, b) => b.date - a.date);
    return out;
  }, [listings, requests, dateFrom, dateTo, materials]);

  const mode = theme?.palette?.mode || "light";

  const rowBorder = sx.eco.border;
  const rowBgA = sx.eco.surfaceA;
  const rowBgB = sx.eco.surfaceB;
  const rowHover = mode === "dark" ? "rgba(110,231,168,0.10)" : "rgba(16,80,50,0.08)";
  const textMuted = sx.eco.textMuted;
  const textMain = sx.eco.textMain;
  const mint = mode === "dark" ? "rgba(110, 231, 168, 0.95)" : "rgba(20, 140, 90, 0.85)";

  const toggle = () => setOpen((v) => !v);

  const eventsCountLabel =
    events.length === 1
      ? `${events.length} ${t("event")}`
      : `${events.length} ${t("events")}`;

  return (
    <Paper elevation={0} sx={sx.cardSx}>
      {/* Header (clickable) */}
      <Box
        sx={{
          ...sx.cardHeaderSx,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={toggle}
        role="button"
        aria-controls={collapseId}
        aria-expanded={open}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            {t("Material Reception Timeline")}
          </Typography>
          <Typography variant="caption" sx={{ color: textMuted }}>
            {t("Confirmed pickups, newest first")}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            variant="outlined"
            label={eventsCountLabel}
            sx={sx.chipSx}
            onClick={(e) => e.stopPropagation()} // prevent toggling when clicking chip
          />

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            aria-label={open ? t("Collapse timeline") : t("Expand timeline")}
            sx={{
              color: textMuted,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 160ms ease",
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Collapsible body */}
      <Collapse in={open} timeout={180}>
        <Box id={collapseId} sx={{ p: 2, pt: 1.5 }}>
          {events.length === 0 && (
            <Typography variant="body2" sx={{ opacity: 0.7, color: textMuted }}>
              {t("No confirmed pickups in selected period.")}
            </Typography>
          )}

          <Stack spacing={1.25}>
            {events.map((evt, idx) => (
              <Box
                key={evt.id || `${evt.dateLabel}-${idx}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "110px 1fr auto" },
                  gap: 1.5,
                  alignItems: "center",
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${rowBorder}`,
                  backgroundColor: rowBgA,
                  backgroundImage: `linear-gradient(135deg, ${rowBgA} 0%, ${rowBgB} 100%)`,
                  color: textMain,
                  transition: "background-color 0.55s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.55s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: rowHover,
                    boxShadow: sx.eco.glow,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                  <FontAwesomeIcon icon={faTruck} style={{ fontSize: "0.7rem", color: mint, opacity: 0.7 }} />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ ...sx.numberCellSx, color: textMuted, display: "block" }}
                    >
                      {evt.dateLabel}
                    </Typography>
                    {getTimeAgo(evt.dateLabel) && (
                      <Typography variant="caption" sx={{ color: mint, fontSize: "0.65rem", fontWeight: 600 }}>
                        {getTimeAgo(evt.dateLabel)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ position: "relative", pl: 2 }}>
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 6,
                      bottom: 6,
                      width: 2,
                      backgroundColor: mode === "dark" ? "rgba(110,231,168,0.22)" : "rgba(16,80,50,0.18)",
                      borderRadius: 2,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: -4,
                      top: 14,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: mint,
                      boxShadow:
                        mode === "dark"
                          ? "0 0 10px rgba(110,231,168,0.25)"
                          : "0 0 10px rgba(16,80,50,0.16)",
                    }}
                  />

                  <Typography variant="body2" sx={{ fontWeight: 900, color: textMain }}>
                    {evt.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: textMuted }}>
                    {evt.material} ·{" "}
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>
                      {Number(evt.weight || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>{" "}
                    {t("lbs")}
                  </Typography>
                </Box>

                <Chip size="small" label={evt.material} variant="outlined" sx={sx.chipSx} />
              </Box>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default MaterialReceptionTimeline;
